import axios, { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { connectionManager } from "./connectionManager";
import config from "../config/env";

// Declare window._env_ for runtime configuration
declare global {
  interface Window {
    _env_?: {
      REACT_APP_API_URL?: string;
      [key: string]: any;
    };
  }
}

/**
 * Custom error type for circuit breaker errors
 */
export interface CircuitBreakerError extends Error {
  isCircuitOpen?: boolean;
}

// Backend API base URL
// Priority: 1) Runtime config (Docker), 2) Config service (from env), 3) Default fallback
// For local development, use .env.development file
// For Docker deployment, pass REACT_APP_API_URL when running container

const API_BASE_URL =
  (window._env_ && window._env_.REACT_APP_API_URL) || // Runtime config (Docker)
  config.api.baseUrl || // Config service (from environment)
  "http://k8s-ns2nimbu-ns2nimbu-e39c1465d9-d2118dfacf6cf7ec.elb.us-east-2.amazonaws.com/api/v1/"; // Fallback

console.log("******window=", window);
console.log("******API_BASE_URL=", API_BASE_URL);
/**
 * Axios instance for API requests
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000, // 30 second timeout (increased for slow backend)
});

// Request interceptor - Check circuit breaker before making requests
apiClient.interceptors.request.use(
  (
    config: InternalAxiosRequestConfig
  ): InternalAxiosRequestConfig | Promise<InternalAxiosRequestConfig> => {
    if (!connectionManager.canMakeRequest()) {
      const error: CircuitBreakerError = new Error(
        "Backend connection unavailable. Please check if the backend server is running and accessible."
      );
      error.isCircuitOpen = true;
      return Promise.reject(error);
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Track successes and failures
apiClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Record successful request
    connectionManager.recordSuccess();
    return response;
  },
  (error: any) => {
    // Don't record failure for circuit breaker errors (to avoid double counting)
    if (!error.isCircuitOpen) {
      connectionManager.recordFailure(error);
    }
    return Promise.reject(error);
  }
);

// Export connection manager for components to use
export { connectionManager };

// ============================================================================
// Service APIs
// ============================================================================

/**
 * Service interface
 */
export interface Service {
  id: string;
  name: string;
  status: string;
  health?: number;
  lastSeen?: string;
  [key: string]: any;
}

/**
 * Service health response
 */
export interface ServiceHealth {
  serviceName: string;
  health: number;
  status: string;
  lastCheck: string;
  [key: string]: any;
}

/**
 * Get all services
 */
export const getServices = async (): Promise<Service[]> => {
  const response = await apiClient.get<Service[]>("/services");
  return response.data;
};

/**
 * Get service health by name
 */
export const getServiceHealth = async (serviceName: string): Promise<ServiceHealth> => {
  const response = await apiClient.get<ServiceHealth>(`/services/${serviceName}/health`);
  return response.data;
};

// ============================================================================
// Metrics APIs
// ============================================================================

/**
 * Metric data point interface
 */
export interface Metric {
  id?: string;
  serviceName: string;
  metricName: string;
  value: number;
  timestamp: string;
  tags?: Record<string, string>;
  [key: string]: any;
}

/**
 * Get metrics for a service/metric combination
 */
export const getMetrics = async (
  serviceName: string,
  metricName: string,
  limit: number = 100
): Promise<Metric[]> => {
  const response = await apiClient.get<Metric[]>(`/metrics/${serviceName}/${metricName}`, {
    params: { limit },
  });
  return response.data;
};

/**
 * Generate real-time metric (for testing)
 */
export const generateRealtimeMetric = async (
  serviceName: string,
  metricName: string
): Promise<Metric> => {
  const response = await apiClient.post<Metric>(`/metrics/realtime`, null, {
    params: { service_name: serviceName, metric_name: metricName },
  });
  return response.data;
};

// ============================================================================
// Anomaly APIs
// ============================================================================

/**
 * Anomaly interface
 */
export interface Anomaly {
  id: string;
  serviceName: string;
  metricName: string;
  value: number;
  threshold?: number;
  severity: string;
  status: string;
  detectedAt: string;
  resolvedAt?: string;
  description?: string;
  rootCause?: any;
  [key: string]: any;
}

/**
 * Get anomalies with optional filtering
 */
export const getAnomalies = async (
  status: string | null = null,
  serviceName: string | null = null,
  limit: number = 100
): Promise<Anomaly[]> => {
  const params: Record<string, any> = {};
  if (status) params.status = status;
  if (serviceName) params.service_name = serviceName;
  params.limit = limit;

  const response = await apiClient.get<Anomaly[]>("/anomalies", { params });
  return response.data;
};

/**
 * Get anomaly by ID
 */
export const getAnomaly = async (anomalyId: string): Promise<Anomaly> => {
  const response = await apiClient.get<Anomaly>(`/anomalies/${anomalyId}`);
  return response.data;
};

/**
 * Update anomaly status
 */
export const updateAnomalyStatus = async (anomalyId: string, status: string): Promise<Anomaly> => {
  const response = await apiClient.put<Anomaly>(`/anomalies/${anomalyId}/status`, null, {
    params: { status },
  });
  return response.data;
};

/**
 * Analyze root cause for an anomaly
 */
export const analyzeRootCause = async (anomalyId: string): Promise<any> => {
  const response = await apiClient.post(`/anomalies/${anomalyId}/analyze`);
  return response.data;
};

// ============================================================================
// Trace APIs
// ============================================================================

/**
 * Trace interface
 */
export interface Trace {
  id: string;
  traceId: string;
  serviceName: string;
  operationName: string;
  duration: number;
  startTime: string;
  endTime: string;
  status: string;
  spans?: any[];
  [key: string]: any;
}

/**
 * Trace statistics interface
 */
export interface TraceStats {
  totalSpans: number;
  totalDuration: number;
  avgDuration: number;
  criticalPathDuration: number;
  [key: string]: any;
}

/**
 * Get traces with optional filtering
 */
export const getTraces = async (
  limit: number = 50,
  status: string | null = null
): Promise<Trace[]> => {
  const params: Record<string, any> = { limit };
  if (status) params.status = status;
  const response = await apiClient.get<Trace[]>("/traces", { params });
  return response.data;
};

/**
 * Generate a trace (for testing)
 */
export const generateTrace = async (injectAnomaly: boolean = false): Promise<Trace> => {
  const response = await apiClient.post<Trace>("/traces/generate", null, {
    params: { inject_anomaly: injectAnomaly },
  });
  return response.data;
};

/**
 * Get trace by ID
 */
export const getTrace = async (traceId: string): Promise<Trace> => {
  const response = await apiClient.get<Trace>(`/traces/${traceId}`);
  return response.data;
};

/**
 * Get trace statistics
 */
export const getTraceStats = async (traceId: string): Promise<TraceStats> => {
  const response = await apiClient.get<TraceStats>(`/traces/${traceId}/stats`);
  return response.data;
};

/**
 * Get trace critical path
 */
export const getTraceCriticalPath = async (traceId: string): Promise<any> => {
  const response = await apiClient.get(`/traces/${traceId}/critical-path`);
  return response.data;
};

// ============================================================================
// Dashboard APIs
// ============================================================================

/**
 * Dashboard statistics interface
 */
export interface DashboardStats {
  totalServices: number;
  activeIncidents: number;
  openProblems: number;
  criticalAnomalies: number;
  avgResponseTime: number;
  systemHealth: number;
  [key: string]: any;
}

/**
 * Real-time data interface
 */
export interface RealtimeData {
  timestamp: string;
  metrics: Metric[];
  anomalies: Anomaly[];
  [key: string]: any;
}

/**
 * Get dashboard statistics
 */
export const getDashboardStats = async (): Promise<DashboardStats> => {
  const response = await apiClient.get<DashboardStats>("/dashboard/stats");
  return response.data;
};

/**
 * Get real-time dashboard data
 */
export const getRealtimeData = async (): Promise<RealtimeData> => {
  const response = await apiClient.get<RealtimeData>("/dashboard/realtime");
  return response.data;
};

// ============================================================================
// Detection APIs
// ============================================================================

/**
 * Anomaly detection request
 */
export interface AnomalyDetectionRequest {
  service_name: string;
  metric_name: string;
  value: number;
}

/**
 * Anomaly detection response
 */
export interface AnomalyDetectionResponse {
  isAnomaly: boolean;
  confidence: number;
  threshold: number;
  [key: string]: any;
}

/**
 * Detect anomaly for a metric value
 */
export const detectAnomaly = async (
  serviceName: string,
  metricName: string,
  value: number
): Promise<AnomalyDetectionResponse> => {
  const response = await apiClient.post<AnomalyDetectionResponse>("/detect", {
    service_name: serviceName,
    metric_name: metricName,
    value,
  });
  return response.data;
};
